import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  Brain, 
  Star, 
  Heart, 
  Users, 
  Smile, 
  CheckCircle, 
  Lightbulb, 
  Scale,
  ArrowRight
} from 'lucide-react';

export default function PersonalityResultsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    if (user) {
      fetchResults();
    }
  }, [user]);

  const fetchResults = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('personality_assessments')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      if (!data) {
        navigate('/planner');
        return;
      }

      setResults(data);
    } catch (error) {
      console.error('Error fetching results:', error);
      navigate('/planner');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <div className="flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">No Results Found</h2>
              <p className="text-muted-foreground mb-4">
                Please complete the personality assessment first.
              </p>
              <Button onClick={() => navigate('/planner')}>
                Take Assessment
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container py-8 px-4 md:px-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-primary" />
              <CardTitle>Your Personality Profile</CardTitle>
            </div>
            <Badge variant="outline" className="gap-1">
              <Star className="h-3 w-3" />
              Profile Complete
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {/* Display personality results here */}
          <div className="text-center py-12">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-2xl font-medium mb-2">Assessment Complete!</h3>
            <p className="text-muted-foreground mb-6">
              Your comprehensive personality profile is ready to view
            </p>
            <div className="flex justify-center">
              <Button onClick={() => navigate('/profile')} size="lg">
                View Your Full Results
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}